import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, QrCode } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Input } from '@/shared/ui/inputs/Input';
import { Button } from '@/shared/ui/buttons/Button';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import {
  apiPackagesNextName,
  apiPackagesPatch,
  apiPackagesPost,
} from '@/entities/packages';
import {
  FeaturesPackagesParcelPicker,
  packageFormSchema,
  type PackageFormValues,
} from '@/features/packages';
import { cn } from '@/shared/utils/cn';
import { downloadQr } from '@/shared/utils/qr';
import { usePricingConfig } from '@/entities/pricing';
import { normalizeDecimal, sanitizeDecimalInput } from '@/shared/utils/number';
import { roundMoney } from '@/shared/utils/currency';
import { extractErrorMessage, isApiError } from '@/shared/api/errors';
import type {
  ApiPackagesGetItem,
  ApiPackagesParcelPickerItem,
  ApiPackagesPostBody,
  WeightUnit,
} from '@/shared/types/api/packages';
import { WEIGHT_UNIT_VALUES } from '@/shared/types/api/packages';

type Props = {
  mode: 'create' | 'edit';
  pkg?: ApiPackagesGetItem | null;
};

const normalizeNumberString = (v: string): string => v.trim().replace(',', '.');

const sortedParcels = (
  pkg: ApiPackagesGetItem | null | undefined,
): ApiPackagesParcelPickerItem[] => {
  if (!pkg) return [];
  return [...pkg.parcels]
    .sort((a, b) => a.order - b.order)
    .map((link) => ({
      id: link.parcel.id,
      trek_id: link.parcel.trek_id,
      name: link.parcel.name,
      cargo_id_code: link.parcel.cargo_id_code,
      products: link.parcel.products,
      passport_series: link.parcel.passport_series,
      pinfl: link.parcel.pinfl,
      fulfillment_status: link.parcel.fulfillment_status,
      created_at: link.parcel.created_at,
    }));
};

export const WidgetsPackageFormMain = ({ mode, pkg = null }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const isEdit = mode === 'edit';

  const [attached, setAttached] = useState<ApiPackagesParcelPickerItem[]>(() =>
    sortedParcels(pkg),
  );
  const [parcelsError, setParcelsError] = useState<string | null>(null);

  // Re-sync attached parcels when edit data arrives.
  useEffect(() => {
    if (!isEdit) return;
    setAttached(sortedParcels(pkg));
  }, [isEdit, pkg]);

  const form = useForm<PackageFormValues>({
    resolver: zodResolver(packageFormSchema),
    defaultValues: {
      name: pkg?.name ?? '',
      weight:
        pkg?.weight !== undefined && pkg?.weight !== null ? String(pkg.weight) : '',
      weight_unit: pkg?.weight_unit ?? 'kg',
    },
  });

  useEffect(() => {
    if (!isEdit) return;
    form.reset({
      // Keep an auto-assigned name if the package itself has none.
      name: pkg?.name || form.getValues('name') || '',
      weight:
        pkg?.weight !== undefined && pkg?.weight !== null ? String(pkg.weight) : '',
      weight_unit: pkg?.weight_unit ?? 'kg',
    });
    // form is stable from useForm; deliberately omit to avoid reset loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, pkg]);

  const goBack = (): void => {
    navigate('/packages');
  };

  const toBody = (values: PackageFormValues): ApiPackagesPostBody => {
    const weightRaw = (values.weight ?? '').toString();
    const normalizedWeight = normalizeNumberString(weightRaw);
    return {
      name: values.name.trim(),
      weight: normalizedWeight === '' ? null : normalizedWeight,
      weight_unit: values.weight_unit,
      parcel_ids: attached.map((p) => p.id),
    };
  };

  // The package NAME is always machine-assigned (no manual "generate"
  // button): on every mount of the create page — and for a legacy edit-target
  // that has no name — fetch the next unique ``YYMM-N`` from the server and
  // prefill the (read-only) field. The server counter advances on each fetch,
  // so a refresh/cancel simply yields the next number; names never repeat.
  const fetchName = useCallback(async (): Promise<void> => {
    try {
      const res = await apiPackagesNextName();
      form.setValue('name', res.name, { shouldDirty: true, shouldValidate: true });
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
    // form/toast are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire-once guard: StrictMode double-invokes effects in dev and the edit
  // payload may re-render; never burn more than one number per page visit.
  const autoNamedRef = useRef(false);
  useEffect(() => {
    if (autoNamedRef.current) return;
    if (isEdit) {
      if (!pkg || pkg.name) return; // wait for data; keep a saved name as-is
    }
    autoNamedRef.current = true;
    void fetchName();
  }, [isEdit, pkg, fetchName]);

  const create = useMutate({
    api: (values: PackageFormValues) => apiPackagesPost(toBody(values)),
    invalidateKeys: [queryKeys.packages.all, queryKeys.parcels.all],
    onSuccess: () => {
      toast.success(t('packages.toasts.created'));
      navigate('/packages');
    },
    onError: (e, m) => {
      toast.error(m);
      // Name was claimed by someone else in the meantime -> hand out a fresh
      // one so the user can just press Save again.
      const data = isApiError(e) ? (e.response?.data as Record<string, unknown> | undefined) : undefined;
      if (data && 'name' in data) void fetchName();
    },
  });

  const update = useMutate({
    api: (values: PackageFormValues) =>
      apiPackagesPatch(pkg?.id ?? '', toBody(values)),
    invalidateKeys: [queryKeys.packages.all, queryKeys.parcels.all],
    onSuccess: () => {
      toast.success(t('packages.toasts.updated'));
      navigate('/packages');
    },
    onError: (_e, m) => toast.error(m),
  });

  const isPending = create.isPending || update.isPending;

  const onSubmit = form.handleSubmit((values) => {
    if (attached.length === 0) {
      setParcelsError(t('packages.form.errors.parcelsRequired'));
      return;
    }
    setParcelsError(null);
    if (isEdit) update.mutate(values);
    else create.mutate(values);
  });

  const handleParcelsChange = (next: ApiPackagesParcelPickerItem[]): void => {
    setAttached(next);
    if (next.length > 0 && parcelsError) {
      setParcelsError(null);
    }
  };

  const pricing = usePricingConfig();

  const name = form.watch('name');
  const handleDownloadQr = (): Promise<void> => downloadQr(name, `${name}.png`);

  const perKg = Number(normalizeDecimal(pricing.data?.package_price_per_kg ?? '0'));
  const w = Number(normalizeDecimal((form.watch('weight') ?? '').toString()));
  const unit = form.watch('weight_unit');
  const kg = unit === 'kg' ? w : w / 1000;
  // Mirrors PackageDetailView.patch: the stored price is a snapshot and is
  // only re-computed (at today's per-kg rate) when weight/unit really change.
  const savedW = pkg?.weight !== undefined && pkg?.weight !== null ? Number(pkg.weight) : null;
  const keepsSavedPrice =
    isEdit &&
    !!pkg &&
    pkg.weight_unit === unit &&
    (savedW === null ? !(Number.isFinite(w) && (form.watch('weight') ?? '').toString().trim() !== '') : savedW === w);
  const computedPrice = keepsSavedPrice
    ? Number(pkg?.price) || 0
    : roundMoney(Number.isFinite(w) ? kg * perKg : 0);

  const weightReg = form.register('weight');

  const nameError = form.formState.errors.name?.message;
  const weightError = form.formState.errors.weight?.message;

  const title = isEdit
    ? t('packages.form.editTitle')
    : t('packages.form.createTitle');

  return (
    <>
      <PageHeader title={title} showBack backTo="/packages" />
      <form
        onSubmit={onSubmit}
        className="space-y-6 px-4 py-4 lg:px-6 lg:py-6"
        data-cy="package-form"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1">
                <Input
                  label={t('packages.form.fields.name')}
                  required
                  disabled
                  error={nameError ? t(nameError) : undefined}
                  {...form.register('name')}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={!name}
                  leftIcon={<QrCode className="h-4 w-4" />}
                  onClick={() => void handleDownloadQr()}
                >
                  {t('packages.form.downloadQr')}
                </Button>
              </div>
            </div>
          </div>
          <Input
            label={t('packages.form.fields.price')}
            type="text"
            inputMode="decimal"
            prefix="$"
            disabled
            value={computedPrice.toFixed(2)}
          />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="package-weight-input" className="text-sm font-medium text-ink-soft">
              {t('packages.form.fields.weight')}
            </label>
            <div
              className={cn(
                'flex items-stretch rounded-lg border bg-white shadow-sm transition-colors',
                weightError
                  ? 'border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-200'
                  : 'border-slate-300 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-200',
              )}
            >
              <input
                id="package-weight-input"
                type="text"
                inputMode="decimal"
                aria-invalid={Boolean(weightError)}
                aria-describedby={weightError ? 'package-weight-error' : undefined}
                className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-sm text-ink outline-none placeholder:text-ink-faint disabled:cursor-not-allowed disabled:opacity-60"
                {...weightReg}
                onChange={(e) => {
                  const s = sanitizeDecimalInput(e.target.value);
                  if (s !== e.target.value) e.target.value = s;
                  void weightReg.onChange(e);
                }}
              />
              <div className="flex items-center border-l border-slate-200 pr-3">
                <select
                  aria-label={t('packages.form.fields.weightUnit')}
                  className="cursor-pointer appearance-none bg-transparent py-2.5 pl-3 pr-7 text-sm text-ink outline-none disabled:cursor-not-allowed disabled:opacity-60"
                  {...form.register('weight_unit')}
                >
                  {WEIGHT_UNIT_VALUES.map((unit) => (
                    <option key={unit} value={unit}>
                      {t(`packages.form.weightUnit.${unit as WeightUnit}`)}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none -ml-6 h-4 w-4 text-ink-muted" />
              </div>
            </div>
            {weightError && (
              <p id="package-weight-error" className="text-xs font-medium text-red-600">
                {t(weightError)}
              </p>
            )}
          </div>
        </div>

        <FeaturesPackagesParcelPicker
          value={attached}
          onChange={handleParcelsChange}
          error={parcelsError ?? undefined}
        />

        <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur lg:-mx-6 lg:px-6">
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={goBack}
              disabled={isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={isPending}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};
